import { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, KeyRound, Users, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader from '@/components/CloudinaryUploader';

const newId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));

export const RetreatsModuleEditor = ({ data, onChange, siteId, stripeKey }) => {
  const [expanded, setExpanded] = useState(null);
  const [keyInput, setKeyInput] = useState(stripeKey || '');
  const [savingKey, setSavingKey] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [loadingBookings, setLoadingBookings] = useState(false);

  const retreats = data.retreats || [];

  const updateRetreat = (idx, updates) => {
    const next = [...retreats];
    next[idx] = { ...next[idx], ...updates };
    onChange({ ...data, retreats: next });
  };

  const addRetreat = () => {
    const retreat = {
      id: newId(),
      title: 'New Retreat',
      date_label: '',
      start_date: '',
      end_date: '',
      description: '',
      includes: [],
      image: '',
      capacity: 16,
      price_options: [
        { id: newId(), label: 'Single Occupancy', price: 0 },
        { id: newId(), label: 'Double Occupancy — shared room', price: 0 },
      ],
      booking_open: false,
      is_past: false,
    };
    onChange({ ...data, retreats: [retreat, ...retreats] });
    setExpanded(retreat.id);
  };

  const removeRetreat = (idx) => {
    onChange({ ...data, retreats: retreats.filter((_, i) => i !== idx) });
  };

  const updateOption = (idx, oIdx, updates) => {
    const opts = [...(retreats[idx].price_options || [])];
    opts[oIdx] = { ...opts[oIdx], ...updates };
    updateRetreat(idx, { price_options: opts });
  };

  const saveStripeKey = async () => {
    setSavingKey(true);
    try {
      await api.put(`/sites/${siteId}`, { stripe_api_key: keyInput.trim() || null });
      toast.success(keyInput.trim() ? 'Stripe key saved — bookings are live' : 'Stripe key removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save key');
    } finally {
      setSavingKey(false);
    }
  };

  const loadBookings = async () => {
    setLoadingBookings(true);
    try {
      const res = await api.get(`/retreats/${siteId}/bookings`);
      setBookings(res.data);
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoadingBookings(false);
    }
  };

  useEffect(() => { loadBookings(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6">
      {/* Module settings */}
      <div className="space-y-3">
        <div className="space-y-2">
          <Label>Section Title</Label>
          <Input
            value={data.title || ''}
            onChange={(e) => onChange({ ...data, title: e.target.value })}
            placeholder="Retreats"
            data-testid="retreats-title-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Intro Text</Label>
          <Textarea
            value={data.intro || ''}
            onChange={(e) => onChange({ ...data, intro: e.target.value })}
            placeholder="A short introduction shown under the section title..."
            rows={2}
          />
        </div>
      </div>

      {/* Stripe key */}
      <div className="p-4 bg-slate-700/40 border border-slate-600/50 rounded-xl space-y-3" data-testid="retreats-stripe-settings">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-amber-400" />
          <Label className="font-semibold text-slate-200">Stripe Secret Key (this site's own account)</Label>
        </div>
        <p className="text-xs text-slate-400">
          Payments go directly to the client's Stripe account. Paste their secret key (sk_live_… or sk_test_…).
          {stripeKey ? ' A key is currently set.' : ' No key set — booking buttons will show "not available".'}
        </p>
        <div className="flex gap-2">
          <Input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="sk_live_..."
            className="font-mono text-sm"
            data-testid="stripe-key-input"
          />
          <Button onClick={saveStripeKey} disabled={savingKey} className="bg-amber-600 hover:bg-amber-500 whitespace-nowrap" data-testid="stripe-key-save-btn">
            {savingKey ? 'Saving…' : 'Save Key'}
          </Button>
        </div>
      </div>

      {/* Retreats list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-base font-semibold">Retreats ({retreats.length})</Label>
          <Button size="sm" onClick={addRetreat} className="bg-orange-500 hover:bg-orange-400" data-testid="add-retreat-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Retreat
          </Button>
        </div>

        {retreats.map((retreat, idx) => {
          const isOpen = expanded === retreat.id;
          const summary = bookings?.summary?.[retreat.id];
          return (
            <div key={retreat.id} className="border border-slate-600/50 rounded-xl overflow-hidden bg-slate-700/30">
              <button
                className="w-full flex items-center justify-between p-4 text-left"
                onClick={() => setExpanded(isOpen ? null : retreat.id)}
                data-testid={`retreat-row-${idx}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  {retreat.image && <img src={retreat.image} alt="" className="w-14 h-10 rounded object-cover flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="font-medium text-white truncate">{retreat.title}</p>
                    <p className="text-xs text-slate-400 truncate">
                      {retreat.date_label || 'No dates'} • {retreat.capacity} places
                      {summary ? ` • ${summary.paid_places} paid` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {retreat.is_past ? (
                    <span className="text-[10px] font-semibold uppercase text-slate-400 bg-slate-600/50 px-2 py-0.5 rounded-full">Past</span>
                  ) : retreat.booking_open ? (
                    <span className="text-[10px] font-semibold uppercase text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-full">Booking Open</span>
                  ) : (
                    <span className="text-[10px] font-semibold uppercase text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded-full">Interest Only</span>
                  )}
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </button>

              {isOpen && (
                <div className="p-4 pt-0 space-y-4 border-t border-slate-600/40">
                  <div className="grid sm:grid-cols-2 gap-3 mt-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Title</Label>
                      <Input value={retreat.title || ''} onChange={(e) => updateRetreat(idx, { title: e.target.value })} data-testid={`retreat-title-input-${idx}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Date Label (shown on site)</Label>
                      <Input
                        value={retreat.date_label || ''}
                        onChange={(e) => updateRetreat(idx, { date_label: e.target.value })}
                        placeholder="e.g. 11 – 13 September 2026"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Start Date</Label>
                      <Input type="date" value={retreat.start_date || ''} onChange={(e) => updateRetreat(idx, { start_date: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">End Date</Label>
                      <Input type="date" value={retreat.end_date || ''} onChange={(e) => updateRetreat(idx, { end_date: e.target.value })} />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Description</Label>
                    <Textarea rows={3} value={retreat.description || ''} onChange={(e) => updateRetreat(idx, { description: e.target.value })} />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">What's Included (one per line)</Label>
                    <Textarea
                      rows={4}
                      value={(retreat.includes || []).join('\n')}
                      onChange={(e) => updateRetreat(idx, { includes: e.target.value.split('\n').filter(l => l.trim()) })}
                      placeholder={"3 day / 2 night stay\nAll meals included"}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Image</Label>
                    <CloudinaryUploader
                      currentImage={retreat.image || null}
                      onUpload={(url) => updateRetreat(idx, { image: url })}
                      onRemove={() => updateRetreat(idx, { image: '' })}
                      folder="sites/retreats"
                      placeholder="Upload retreat image"
                      className="max-w-[260px]"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Capacity (total places)</Label>
                      <Input
                        type="number"
                        min={1}
                        value={retreat.capacity ?? 16}
                        onChange={(e) => updateRetreat(idx, { capacity: parseInt(e.target.value || '0', 10) })}
                        data-testid={`retreat-capacity-input-${idx}`}
                      />
                    </div>
                  </div>

                  {/* Price options */}
                  <div className="space-y-2">
                    <Label className="text-xs">Price Options (per person)</Label>
                    {(retreat.price_options || []).map((o, oIdx) => (
                      <div key={o.id} className="flex gap-2 items-center">
                        <Input
                          value={o.label}
                          onChange={(e) => updateOption(idx, oIdx, { label: e.target.value })}
                          placeholder="Single Occupancy"
                          className="flex-1"
                        />
                        <div className="relative w-32">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                          <Input
                            type="number"
                            min={0}
                            value={o.price}
                            onChange={(e) => updateOption(idx, oIdx, { price: parseFloat(e.target.value || '0') })}
                            className="pl-7"
                            data-testid={`retreat-price-input-${idx}-${oIdx}`}
                          />
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300 flex-shrink-0"
                          onClick={() => updateRetreat(idx, { price_options: retreat.price_options.filter((_, i) => i !== oIdx) })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-slate-600 text-slate-300"
                      onClick={() => updateRetreat(idx, { price_options: [...(retreat.price_options || []), { id: newId(), label: '', price: 0 }] })}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Option
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-6 pt-1">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={retreat.booking_open === true}
                        onCheckedChange={(c) => updateRetreat(idx, { booking_open: c })}
                        data-testid={`retreat-booking-open-toggle-${idx}`}
                      />
                      <Label className="text-xs">Booking open (Stripe)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={retreat.is_past === true}
                        onCheckedChange={(c) => updateRetreat(idx, { is_past: c })}
                        data-testid={`retreat-past-toggle-${idx}`}
                      />
                      <Label className="text-xs">Past retreat (archive)</Label>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 ml-auto"
                      onClick={() => removeRetreat(idx)}
                      data-testid={`retreat-delete-btn-${idx}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bookings */}
      <div className="p-4 bg-slate-700/40 border border-slate-600/50 rounded-xl space-y-3" data-testid="retreats-bookings-panel">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-emerald-400" />
            <Label className="font-semibold text-slate-200">Bookings</Label>
          </div>
          <Button size="sm" variant="ghost" onClick={loadBookings} disabled={loadingBookings} className="text-slate-300">
            {loadingBookings ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
        {bookings && Object.keys(bookings.summary || {}).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(bookings.summary).map(([rid, s]) => (
              <span key={rid} className="text-xs bg-slate-600/50 text-slate-200 px-2.5 py-1 rounded-full">
                {s.retreat_title}: <b>{s.paid_places} paid</b> • £{Number(s.revenue).toLocaleString('en-GB')}
              </span>
            ))}
          </div>
        )}
        {bookings && bookings.bookings.length === 0 && (
          <p className="text-xs text-slate-400">No bookings yet.</p>
        )}
        {bookings && bookings.bookings.length > 0 && (
          <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-600/40">
            <table className="w-full text-xs">
              <thead className="bg-slate-800/60 text-slate-400 sticky top-0">
                <tr>
                  <th className="text-left p-2">Name</th>
                  <th className="text-left p-2">Retreat</th>
                  <th className="text-left p-2">Option</th>
                  <th className="text-center p-2">Qty</th>
                  <th className="text-right p-2">Amount</th>
                  <th className="text-right p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.bookings.map((b) => (
                  <tr key={b.id} className="border-t border-slate-700/50 text-slate-200">
                    <td className="p-2">
                      <p className="font-medium">{b.name || '—'}</p>
                      <p className="text-slate-400">{b.email}</p>
                    </td>
                    <td className="p-2">{b.retreat_title}</td>
                    <td className="p-2">{b.option_label}</td>
                    <td className="p-2 text-center">{b.quantity}</td>
                    <td className="p-2 text-right">£{Number(b.amount).toLocaleString('en-GB')}</td>
                    <td className="p-2 text-right">
                      <span className={cn(
                        "px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase",
                        b.payment_status === 'paid' ? "bg-emerald-500/15 text-emerald-400" :
                        b.payment_status === 'initiated' ? "bg-amber-500/15 text-amber-400" :
                        "bg-slate-600/40 text-slate-400"
                      )}>
                        {b.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
